import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { queryDaily, queryYears } from "@/apis";
import { cdnUrl, isJsonString } from "@/utils";
import { mapStateToProps } from "@/store";
import "./styles/search-sheet.scss";

const PAGE_SIZE = 10;
const pad2 = (n) => String(n).padStart(2, "0");

const QUICK_RANGES = [
    { key: "all", label: "全部" },
    { key: "this-year", label: "今年" },
    { key: "last-year", label: "去年" },
    { key: "3y", label: "近三年" },
    { key: "5y", label: "近五年" },
];

/**
 * 把快捷档/自定义起止换算成接口的 from/to（'YYYY-MM' 闭区间，
 * 后端按北京时间把两端月份完整包含）。null 表示不限时间。
 */
const resolveRange = (quick, customFrom, customTo) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = pad2(now.getMonth() + 1);
    switch (quick) {
        case "this-year":
            return { from: `${year}-01`, to: `${year}-12` };
        case "last-year":
            return { from: `${year - 1}-01`, to: `${year - 1}-12` };
        case "3y":
            return { from: `${year - 2}-${month}`, to: `${year}-12` };
        case "5y":
            return { from: `${year - 4}-${month}`, to: `${year}-12` };
        case "custom":
            if (!customFrom || !customTo) return null;
            return {
                from: `${customFrom.y}-${pad2(customFrom.m)}`,
                to: `${customTo.y}-${pad2(customTo.m)}`,
            };
        default:
            return null;
    }
};

/** 正文摘要：2021 起是 Draft.js raw JSON，先拼 blocks 文本再剥标签 */
const excerptOf = (txt = "", max = 64) => {
    let text = txt;
    if (isJsonString(txt)) {
        try {
            text = JSON.parse(txt)
                .blocks.map((block) => block.text || "")
                .join(" ");
        } catch {
            text = txt;
        }
    }
    text = String(text)
        .replace(/<[^>]+>/g, "")
        .replace(/\s+/g, " ")
        .trim();
    return text.length > max ? `${text.slice(0, max)}…` : text;
};

/**
 * 列表缩略图：取第一条媒体（type=video 的 key 本身就是封面帧），
 * 拼七牛方形裁剪参数。src 已带缩放参数的（视频封面）直接用。
 */
const thumbOf = (item) => {
    const first = (item?.images || []).find((m) => m && (m.key || m.src));
    if (!first) return null;
    const base = cdnUrl(first.key || first.src);
    if (!base) return null;
    if (base.includes("?")) return base;
    return `${base}?imageView2/1/w/144/h/144`;
};

function SearchSheet({ open, onClose, system = {} }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const inputRef = useRef(null);
    const panelRef = useRef(null);
    const dragRef = useRef(null);
    const reqSeq = useRef(0);
    const debounceRef = useRef(null);

    // 面板只在首页可见；进详情时保持挂载与状态（open 不关），
    // 返回后原样恢复。visible 只管显示，不影响查询状态。
    const visible = open && pathname === "/";
    const [panelHeight, setPanelHeight] = useState(null); // px，null = 自适应

    const [keyword, setKeyword] = useState(""); // 输入框即时值
    const [committedQ, setCommittedQ] = useState(""); // 防抖后真正参与查询的关键词
    const [quick, setQuick] = useState("all");
    const [customFrom, setCustomFrom] = useState(null); // {y, m}
    const [customTo, setCustomTo] = useState(null); // {y, m}
    const [child, setChild] = useState(0);
    const [years, setYears] = useState([]); // [{year, total}]
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);

    const configuredChildren = useMemo(
        () =>
            Object.values(system?.category || {})
                .map((item) => ({ ...item, no: Number(item.no) }))
                .filter((item) => Number.isInteger(item.no) && item.no > 0)
                .sort((a, b) => a.no - b.no),
        [system],
    );

    // 必须 memo：range 每次渲染新建的话，buildParams/fetchResults 引用链会
    // 全部失效，查询 effect 每帧重跑 → 无限请求（线上踩过）。
    const range = useMemo(
        () => resolveRange(quick, customFrom, customTo),
        [quick, customFrom, customTo],
    );
    const [advanced, setAdvanced] = useState(false); // 高级筛选折叠区开关

    const buildParams = useCallback(
        (nextPage) => {
            const params = { page: nextPage, rows: PAGE_SIZE };
            if (committedQ) params.q = committedQ;
            if (child) params.child = child;
            if (range) {
                params.from = range.from;
                params.to = range.to;
            }
            return params;
        },
        [committedQ, child, range],
    );

    const fetchResults = useCallback(
        async (nextPage, append) => {
            const reqId = ++reqSeq.current;
            setLoading(true);
            try {
                const ret = await queryDaily(buildParams(nextPage));
                if (reqId !== reqSeq.current) return;
                if (ret.code === 200 && Array.isArray(ret.data)) {
                    setItems((prev) =>
                        append ? [...prev, ...ret.data] : ret.data,
                    );
                    setPage(nextPage);
                    setHasMore(ret.data.length >= PAGE_SIZE);
                } else if (!append) {
                    setItems([]);
                    setHasMore(false);
                }
            } catch (error) {
                console.error("搜索记录失败", error);
                if (reqId === reqSeq.current) {
                    setItems([]);
                    setHasMore(false);
                }
            } finally {
                if (reqId === reqSeq.current) setLoading(false);
            }
        },
        [buildParams],
    );

    // 条件变化：重查第一页 + 刷新年份计数。
    // 首查放到微任务，避免在 effect 内同步 setState（react-hooks 规则）；
    // reqSeq 必须在微任务内自增后再捕获代际，否则 queryYears 的响应会被误判为旧请求丢弃。
    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        void Promise.resolve().then(() => {
            if (cancelled) return;
            fetchResults(1, false);
            const reqId = reqSeq.current;
            queryYears({
                q: committedQ || undefined,
                child: child || undefined,
            })
                .then((ret) => {
                    if (cancelled || reqId !== reqSeq.current) return;
                    if (ret.code === 200 && Array.isArray(ret.data)) {
                        setYears(ret.data);
                    }
                })
                .catch((error) => console.error("读取年份统计失败", error));
        });
        return () => {
            cancelled = true;
        };
    }, [open, committedQ, child, quick, customFrom, customTo, fetchResults]);

    // 打开时聚焦输入框、锁定背景滚动（仅首页可见时才锁）
    useEffect(() => {
        if (!visible) return undefined;
        inputRef.current?.focus();
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [visible]);

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    const handleKeywordChange = (event) => {
        const value = event.target.value;
        setKeyword(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(
            () => setCommittedQ(value.trim().slice(0, 50)),
            400,
        );
    };

    const handleKeywordKeyDown = (event) => {
        if (event.key !== "Enter") return;
        clearTimeout(debounceRef.current);
        setCommittedQ(keyword.trim().slice(0, 50));
    };

    /** 点年份 chip：把该年整年填进自定义起止 */
    const pickYear = (year) => {
        setQuick("custom");
        setCustomFrom({ y: year, m: 1 });
        setCustomTo({ y: year, m: 12 });
    };

    /** 自定义起止里保证 from <= to，反了就交换 */
    const setCustomBound = (which, value) => {
        const toNumber = (bound) => bound.y * 12 + bound.m;
        let nextFrom = which === "from" ? value : customFrom;
        let nextTo = which === "to" ? value : customTo;
        if (nextFrom && nextTo && toNumber(nextFrom) > toNumber(nextTo)) {
            [nextFrom, nextTo] = [nextTo, nextFrom];
        }
        setCustomFrom(nextFrom);
        setCustomTo(nextTo);
    };

    const clearAll = () => {
        clearTimeout(debounceRef.current);
        setKeyword("");
        setCommittedQ("");
        setQuick("all");
        setCustomFrom(null);
        setCustomTo(null);
        setChild(0);
    };

    const yearOptions = useMemo(() => {
        const dataMin = years.length ? years[0].year : 2017;
        const min = Math.min(dataMin, 2017);
        const max = new Date().getFullYear();
        const out = [];
        for (let y = max; y >= min; y -= 1) out.push(y);
        return out;
    }, [years]);

    const hasFilters = Boolean(committedQ || range || child);
    const summaryParts = [];
    if (range) {
        summaryParts.push(`${range.from} 至 ${range.to}`);
    }
    if (child) {
        const matched = configuredChildren.find((item) => item.no === child);
        if (matched) summaryParts.push(matched.name);
    }
    if (committedQ) summaryParts.push(`「${committedQ}」`);

    const renderPicker = (which, value) => (
        <div className="search-sheet__pickers">
            <select
                aria-label={which === "from" ? "起始年份" : "结束年份"}
                value={value?.y ?? ""}
                onChange={(event) =>
                    setCustomBound(which, {
                        y: Number(event.target.value),
                        m: value?.m ?? 1,
                    })
                }
            >
                <option value="" disabled>
                    年
                </option>
                {yearOptions.map((y) => (
                    <option key={y} value={y}>
                        {y}
                    </option>
                ))}
            </select>
            <select
                aria-label={which === "from" ? "起始月份" : "结束月份"}
                value={value?.m ?? ""}
                onChange={(event) =>
                    setCustomBound(which, {
                        y: value?.y ?? yearOptions[0],
                        m: Number(event.target.value),
                    })
                }
            >
                <option value="" disabled>
                    月
                </option>
                {Array.from({ length: 12 }, (_, idx) => idx + 1).map((m) => (
                    <option key={m} value={m}>
                        {pad2(m)}
                    </option>
                ))}
            </select>
        </div>
    );

    /** 拖拽 grip 调整面板高度；下拉过低松手直接关闭 */
    const handleGripPointerDown = (event) => {
        event.preventDefault();
        dragRef.current = {
            startY: event.clientY,
            startH: panelRef.current?.getBoundingClientRect().height ?? 0,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleGripPointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const vh = window.innerHeight;
        // 上拖 dy>0 → 面板变高；限制在 30dvh ~ 88dvh
        const dy = drag.startY - event.clientY;
        setPanelHeight(
            Math.min(Math.max(drag.startH + dy, vh * 0.3), vh * 0.88),
        );
    };

    const handleGripPointerUp = () => {
        if (!dragRef.current) return;
        dragRef.current = null;
        if (panelHeight != null && panelHeight < window.innerHeight * 0.42) {
            onClose();
        }
    };

    return (
        <div
            className={`search-sheet ${visible ? "open" : ""}`.trim()}
            aria-hidden={!visible}
        >
            <div className="search-sheet__mask" onClick={onClose} />
            <section
                ref={panelRef}
                className="search-sheet__panel"
                role="dialog"
                aria-modal="true"
                aria-label="搜索成长记录"
                style={
                    panelHeight != null ? { height: panelHeight } : undefined
                }
            >
                <div
                    className="search-sheet__grip"
                    aria-hidden="true"
                    onPointerDown={handleGripPointerDown}
                    onPointerMove={handleGripPointerMove}
                    onPointerUp={handleGripPointerUp}
                    onPointerCancel={handleGripPointerUp}
                />
                <header className="search-sheet__head">
                    <span className="search-sheet__title">搜索成长记录</span>
                    <button
                        type="button"
                        className="search-sheet__done"
                        onClick={onClose}
                    >
                        完成
                    </button>
                </header>
                <div className="search-sheet__body">
                    <div className="search-sheet__searchbox">
                        <input
                            ref={inputRef}
                            type="search"
                            placeholder="搜索正文、地点…"
                            value={keyword}
                            onChange={handleKeywordChange}
                            onKeyDown={handleKeywordKeyDown}
                            aria-label="搜索关键词"
                        />
                        {keyword && (
                            <button
                                type="button"
                                aria-label="清空关键词"
                                onClick={() => {
                                    clearTimeout(debounceRef.current);
                                    setKeyword("");
                                    setCommittedQ("");
                                }}
                            >
                                ×
                            </button>
                        )}
                    </div>

                    <div className="search-sheet__section">时间</div>
                    <div className="search-sheet__chips">
                        {QUICK_RANGES.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={quick === item.key ? "active" : ""}
                                aria-pressed={quick === item.key}
                                onClick={() => setQuick(item.key)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    {/* 高级筛选：自定义区间 + 年份导航默认收起，保持浮层轻量 */}
                    <button
                        type="button"
                        className={`search-sheet__advtoggle${
                            quick === "custom" && range ? " active" : ""
                        }${advanced ? " open" : ""}`}
                        aria-expanded={advanced}
                        onClick={() => setAdvanced((v) => !v)}
                    >
                        {quick === "custom" && range
                            ? `${range.from} ~ ${range.to}`
                            : "自定义区间"}
                        <i aria-hidden="true">{advanced ? "▴" : "▾"}</i>
                    </button>
                    {advanced && (
                        <div className="search-sheet__advanced">
                            <div className="search-sheet__custom">
                                {renderPicker("from", customFrom)}
                                <span>至</span>
                                {renderPicker("to", customTo)}
                            </div>
                            {years.length > 0 && (
                                <>
                                    <div className="search-sheet__section">
                                        按年份跳（数字为命中条数）
                                    </div>
                                    <div className="search-sheet__chips">
                                        {years.map((item) => (
                                            <button
                                                key={item.year}
                                                type="button"
                                                aria-pressed={Boolean(
                                                    range &&
                                                    range.from ===
                                                        `${item.year}-01` &&
                                                    range.to ===
                                                        `${item.year}-12`,
                                                )}
                                                onClick={() =>
                                                    pickYear(item.year)
                                                }
                                            >
                                                {item.year}
                                                <em>{item.total}</em>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {configuredChildren.length > 0 && (
                        <>
                            <div className="search-sheet__section">是谁</div>
                            <div className="search-sheet__chips">
                                <button
                                    type="button"
                                    className={child === 0 ? "active" : ""}
                                    aria-pressed={child === 0}
                                    onClick={() => setChild(0)}
                                >
                                    全部
                                </button>
                                {configuredChildren.map((item) => (
                                    <button
                                        key={item.no}
                                        type="button"
                                        className={
                                            child === item.no ? "active" : ""
                                        }
                                        aria-pressed={child === item.no}
                                        onClick={() => setChild(item.no)}
                                    >
                                        {item.name}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    <div className="search-sheet__results">
                        <div className="search-sheet__summary">
                            <span>
                                {loading && items.length === 0
                                    ? "搜索中…"
                                    : `命中 ${items.length} 条${
                                          hasMore ? "+" : ""
                                      }${
                                          summaryParts.length
                                              ? ` · ${summaryParts.join(" · ")}`
                                              : ""
                                      }`}
                            </span>
                            {hasFilters && (
                                <button type="button" onClick={clearAll}>
                                    清除全部条件
                                </button>
                            )}
                        </div>
                        {items.length > 0 && (
                            <ul>
                                {items.map((item) => {
                                    const thumb = thumbOf(item);
                                    return (
                                        <li key={item.objectId}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // 不关面板：进详情返回后
                                                    // 搜索状态原样保留
                                                    navigate(
                                                        `/item/${item.objectId}`,
                                                    );
                                                }}
                                            >
                                                {thumb && (
                                                    <img
                                                        className="thumb"
                                                        src={thumb}
                                                        alt=""
                                                        loading="lazy"
                                                    />
                                                )}
                                                <span className="body">
                                                    <span className="date">
                                                        {String(
                                                            item.createdAt,
                                                        ).slice(0, 10)}
                                                        {item.addr_gps?.title
                                                            ? ` · ${item.addr_gps.title}`
                                                            : ""}
                                                    </span>
                                                    <span className="excerpt">
                                                        {excerptOf(item.txt) ||
                                                            "（无文字内容）"}
                                                    </span>
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {!loading && items.length === 0 && (
                            <p className="search-sheet__empty">
                                {hasFilters || committedQ
                                    ? `没有找到${
                                          committedQ
                                              ? `包含「${committedQ}」的`
                                              : "符合条件"
                                      }的记录`
                                    : "输入关键词或选择时间范围开始查找"}
                            </p>
                        )}
                        {hasMore && !loading && (
                            <button
                                type="button"
                                className="search-sheet__more"
                                onClick={() => fetchResults(page + 1, true)}
                            >
                                加载更多
                            </button>
                        )}
                        {loading && items.length > 0 && (
                            <p className="search-sheet__loading">加载中…</p>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

export default connect(mapStateToProps)(SearchSheet);
